import React from 'react';
import { Check, X, DollarSign, Users, MessageCircle, Zap, ArrowRight, AlertCircle } from 'lucide-react';
import { useSEO } from '../utils/seo';

const CompareFreshdesk = () => {
  useSEO({
    title: 'TicketDesk vs Freshdesk — Simpler Help Desk for Small Teams',
    description: 'Compare TicketDesk vs Freshdesk. Unlimited agents, flat pricing at $29/mo. Gmail, Facebook, WhatsApp support + review monitoring included.',
    path: '/compare/freshdesk',
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">TicketDesk</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-gray-600 hover:text-gray-900">Home</a>
              <a href="/register" className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              TicketDesk vs Freshdesk
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Escape per-seat pricing. Get unlimited users without the monthly bill multiplying every time you add someone.
            </p>
          </div>

          {/* Quick Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Freshdesk Card */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Freshdesk Pro</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">$59</div>
                <div className="text-gray-600">per agent/month</div>
                <div className="mt-2 text-sm text-red-600 font-medium">5 agents = $295/month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Multi-channel ticketing</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Automation features</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Per-agent pricing adds up fast</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">AI features cost extra</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Limited to 2,000 tickets/month</span>
                </div>
              </div>
            </div>

            {/* TicketDesk Card */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 shadow-lg border-2 border-purple-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  No Per-Seat Fees
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">TicketDesk Starter</h3>
                <div className="text-4xl font-bold text-purple-600 mb-2">$29</div>
                <div className="text-gray-600">flat rate/month</div>
                <div className="mt-2 text-sm text-green-600 font-medium">Unlimited agents = $29/month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Multi-channel ticketing</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Built-in automation</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Add unlimited agents anytime</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">AI included at all levels</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">200 tickets/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Per-Seat Trap */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">The Per-Seat Pricing Trap</h2>
                <p className="text-gray-700 mb-4">
                  Freshdesk's pricing looks affordable at first. But watch what happens as you grow:
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center bg-white rounded-lg p-4">
                    <span className="text-gray-700">2 team members</span>
                    <span className="font-semibold text-gray-900">$118/month</span>
                  </div>
                  <div className="flex justify-between items-center bg-white rounded-lg p-4">
                    <span className="text-gray-700">5 team members</span>
                    <span className="font-semibold text-gray-900">$295/month</span>
                  </div>
                  <div className="flex justify-between items-center bg-white rounded-lg p-4">
                    <span className="text-gray-700">10 team members</span>
                    <span className="font-semibold text-red-600">$590/month</span>
                  </div>
                </div>
                <p className="text-gray-700 mt-4 font-medium">
                  With TicketDesk? <span className="text-purple-600">Still $29/month.</span> Add 2 people or 200 people - same price.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Feature Comparison
          </h2>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Freshdesk Pro</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-purple-600">TicketDesk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Starting Price</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">$59/agent/month</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">$29/month flat</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Unlimited Agents</td>
                  <td className="px-6 py-4 text-center">
                    <X className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Email Ticketing</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Social Media Channels</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Trustpilot Integration</td>
                  <td className="px-6 py-4 text-center">
                    <X className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">AI-Powered Responses</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Add-on ($29/agent)</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Ticket Limit (base plan)</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">2,000/month</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600">200/month ($29 tier)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Setup Time</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">1-2 hours</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">2 minutes</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Multiple Workspaces</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Enterprise plan</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600">Growth plan ($79)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Free Trial</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">14 days</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">Forever (10 tickets)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cost Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Real Team Scenarios
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Small Team */}
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Small Team (3 people)</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">Freshdesk Pro</div>
                  <div className="text-3xl font-bold text-gray-900">$177</div>
                  <div className="text-sm text-gray-600">per month</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-2">TicketDesk</div>
                  <div className="text-3xl font-bold text-purple-600">$29</div>
                  <div className="text-sm text-purple-600">per month</div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-800 font-semibold">Save $148/month</div>
                <div className="text-sm text-green-700">$1,776/year in savings</div>
              </div>
            </div>

            {/* Growing Team */}
            <div className="bg-gray-50 rounded-xl p-8 border-2 border-purple-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Growing Team (7 people)</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">Freshdesk Pro</div>
                  <div className="text-3xl font-bold text-gray-900">$413</div>
                  <div className="text-sm text-gray-600">per month</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-2">TicketDesk</div>
                  <div className="text-3xl font-bold text-purple-600">$29</div>
                  <div className="text-sm text-purple-600">per month</div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-800 font-semibold">Save $384/month</div>
                <div className="text-sm text-green-700">$4,608/year in savings</div>
              </div>
            </div>

            {/* Larger Team */}
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Larger Team (12 people)</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">Freshdesk Pro</div>
                  <div className="text-3xl font-bold text-gray-900">$708</div>
                  <div className="text-sm text-gray-600">per month</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-2">TicketDesk</div>
                  <div className="text-3xl font-bold text-purple-600">$79</div>
                  <div className="text-sm text-purple-600">per month (Growth)</div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-800 font-semibold">Save $629/month</div>
                <div className="text-sm text-green-700">$7,548/year in savings</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why People Switch */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Teams Leave Freshdesk
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Pricing Anxiety</h3>
              <p className="text-gray-700 mb-4">
                Every time you want to add someone to help, you hesitate because it means another $59/month. That's no way to run a team.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-red-500">
                <div className="text-sm text-gray-700 italic">"We avoided adding team members because of the cost. Now with TicketDesk we can scale freely."</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Hidden Costs</h3>
              <p className="text-gray-700 mb-4">
                Want AI features? Extra cost. Need more automation? Upgrade. Want better reporting? Pay more. The base price is just the beginning.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-orange-500">
                <div className="text-sm text-gray-700 italic">"We were paying for three different add-ons. TicketDesk includes everything."</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">License Management</h3>
              <p className="text-gray-700 mb-4">
                Constantly juggling who has a license. Paying for people who don't use it daily. Dealing with their billing department every month.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                <div className="text-sm text-gray-700 italic">"No more license management headaches. Everyone just has access."</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Switching Takes Minutes
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            No data migration needed - just start fresh with new tickets
          </p>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sign up for TicketDesk</h3>
                <p className="text-gray-700">Takes 30 seconds. Start with our free plan or begin a 7-day trial of any paid plan.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect your Gmail</h3>
                <p className="text-gray-700">Click one button to authorize. New customer emails start flowing in immediately.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Add your whole team</h3>
                <p className="text-gray-700">No per-seat math. Just send invites. Everyone gets full access immediately.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Keep Freshdesk as archive</h3>
                <p className="text-gray-700">Downgrade to free or keep it read-only. Use TicketDesk for all new customer messages.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Common Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Will TicketDesk handle our ticket volume?</h3>
              <p className="text-gray-700">Our Starter plan ($29) handles 200 tickets/month. Growth ($79) handles 1,000. Scale ($149) handles 5,000. That covers most small to medium teams.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I move my historical tickets?</h3>
              <p className="text-gray-700">Most teams don't migrate history - they keep Freshdesk for archive/reference and use TicketDesk going forward. Keeps things simple.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What about Freshdesk's automation rules?</h3>
              <p className="text-gray-700">We have automation too - assignment rules, auto-responses, status updates. Most common Freshdesk automations have TicketDesk equivalents.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I really add unlimited agents?</h3>
              <p className="text-gray-700">Yes. Add 2 people or 200 people - same monthly price. No tricks, no "lite" user tiers. Everyone gets full access.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Stop Paying Per Seat
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join teams saving thousands by switching from Freshdesk
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/register" 
              className="bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a 
              href="/" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Learn More
            </a>
          </div>
          <p className="text-sm opacity-75 mt-6">
            No credit card required • Unlimited team members • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">&copy; 2025 TicketDesk. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CompareFreshdesk;